import mapboxgl, {
  type GeoJSONSource,
  type GeoJSONSourceSpecification,
  type Map,
  type MapMouseEvent,
} from 'mapbox-gl';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { Coordinate } from '@shared/models/geography.model';
import type { Point2D } from '@shared/models/geometry.model';
import {
  areBlueprintStoreyAssignmentsEqual,
  getBlueprintCornerOffsets,
  moveBlueprintUnderlay,
  rotatePoint2DClockwise,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import {
  convertCoordinateToMetricOffset,
  convertMetricOffsetToCoordinate,
  getDistance,
} from '@shared/helpers/geometry/coordinates.helpers';
import {
  softSnapDegreesToStep,
  type StickyAngleSnapState,
} from '../helpers/angle-snap.helpers';
import { BLUEPRINT_ROTATE_CURSOR } from '../helpers/rotate-cursor.helpers';
import {
  MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID,
  MAPBOX_BLUEPRINT_CALIBRATE_SOURCE_ID,
  MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID,
  MAPBOX_BLUEPRINT_HANDLES_LAYER_ID,
  MAPBOX_BLUEPRINT_LAYER_ID,
  MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID,
  MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID,
  MAPBOX_BLUEPRINT_SOURCE_ID,
} from '../map.constants';
import { MapLayerIds } from '../helpers/map-config.constants';
import {
  extractBlueprintInkSnapIndex,
  loadBlueprintImageData,
  snapMetricPointToBlueprintInk,
  type BlueprintInkSnapIndex,
} from '../helpers/blueprint-image-snap.helpers';
import { buildCalibrateMeasureSegments } from '../helpers/blueprint-calibrate-overlay.helpers';
import {
  moveLayerBeforeIfNeeded,
  setLayerVisibilityIfChanged,
  setLayoutPropertyIfChanged,
  stackLayersOnTop,
} from '../helpers/map-layer-order.helpers';
import { shouldResyncBlueprintAfterStyleData } from './blueprint-underlay-style.helpers';
import {
  destroyBlueprintElevatedGl,
  initBlueprintElevatedGl,
  loadBlueprintElevatedTexture,
  renderBlueprintElevatedLayer,
  type BlueprintElevatedGlResources,
  type BlueprintElevatedLayerState,
} from './blueprint-underlay-elevated.helpers';

export type BlueprintInteractionMode = 'locked' | 'transform' | 'calibrate';

/** On-map length label while placing a new measure span. */
export type BlueprintCalibrateLabel = {
  screenPoint: Point2D;
  text: string;
  /** Two points placed — React shows the length popup here. */
  kind: 'pending-input';
};

type BlueprintChangeHandler = (underlay: BlueprintUnderlay) => void;
type CalibratePointHandler = (point: Point2D) => void;
type CalibrateLabelsHandler = (labels: BlueprintCalibrateLabel[]) => void;
/** Stepwise Escape: clear in-progress UI first, then leave the tool. */
export type BlueprintEscapeResult = 'cleared-points' | 'exited-mode';
type EscapeHandler = (result: BlueprintEscapeResult) => void;
type CursorKind =
  'default' | 'grab' | 'grabbing' | 'move' | 'crosshair' | 'rotate';

/** Rubber-band measure stroke. */
const CALIBRATE_LINE_OPACITY = 0.9;
/**
 * Calibrate measure line / ticks (cross marks).
 * Matches import calibrate measure (`theme.palette.custom.focus`).
 */
export const BLUEPRINT_CALIBRATE_COLOR = '#0B99FF';
const CALIBRATE_LINE_WIDTH = 1.5;
const CALIBRATE_TICK_WIDTH = 1.5;
/** Corner rotate handle hit radius in screen pixels. */
const ROTATE_HANDLE_HIT_PX = 14;
/** Soft-snap selected PDF rotation onto 15° steps while dragging a corner. */
const ROTATION_SNAP_STEP_DEG = 15;
const ROTATION_SOFT_SNAP_ENTER_DEG = 3;
const ROTATION_SOFT_SNAP_BREAKOUT_DEG = 6;

type ActiveDrag =
  | {
      kind: 'move';
      startPointer: Point2D;
      startUnderlay: BlueprintUnderlay;
    }
  | {
      kind: 'rotate';
      startPointer: Point2D;
      startUnderlay: BlueprintUnderlay;
      /** Polar angle of the pointer at drag start (matches {@link rotatePoint2DClockwise}). */
      startPointerAngleDeg: number;
      stickySnap: StickyAngleSnapState;
    };

/**
 * True when map sync can skip (same raster + placement + visibility + storey).
 */
const isSameBlueprintUnderlayForMap = (
  left: BlueprintUnderlay | undefined,
  right: BlueprintUnderlay | undefined,
): boolean => {
  if (left === right) return true;
  if (!left || !right) return false;
  return (
    left.id === right.id &&
    left.imageDataUrl === right.imageDataUrl &&
    left.visible === right.visible &&
    left.opacity === right.opacity &&
    left.widthMeters === right.widthMeters &&
    left.heightMeters === right.heightMeters &&
    left.rotationDeg === right.rotationDeg &&
    left.center[0] === right.center[0] &&
    left.center[1] === right.center[1] &&
    left.calibration === right.calibration &&
    // Storey mapping drives elevation — must invalidate when it changes.
    areBlueprintStoreyAssignmentsEqual(
      left.storeyAssignment,
      right.storeyAssignment,
    )
  );
};

/**
 * True when calibrate label payloads are equal for React state updates.
 */
const areCalibrateLabelsEqual = (
  left: readonly BlueprintCalibrateLabel[],
  right: readonly BlueprintCalibrateLabel[],
): boolean => {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  return left.every((label, index) => {
    const other = right[index];
    if (!other) return false;
    return (
      label.text === other.text &&
      label.kind === other.kind &&
      label.screenPoint[0] === other.screenPoint[0] &&
      label.screenPoint[1] === other.screenPoint[1]
    );
  });
};

/**
 * Owns the Mapbox image/raster underlay + transform outline/handles + calibrate overlay.
 */
export class BlueprintUnderlayController {
  private map: Map | undefined;
  private location: Coordinate | undefined;
  private underlay: BlueprintUnderlay | undefined;
  private mode: BlueprintInteractionMode = 'locked';
  private drag: ActiveDrag | undefined;
  private onChange: BlueprintChangeHandler | undefined;
  private onCalibratePoint: CalibratePointHandler | undefined;
  private onCalibrateLabels: CalibrateLabelsHandler | undefined;
  private onEscape: EscapeHandler | undefined;
  /** Last labels pushed to React — avoid setState identity thrash. */
  private lastCalibrateLabels: BlueprintCalibrateLabel[] = [];
  private listenersAttached = false;
  private spaceHeld = false;
  private isPanningWithSpace = false;
  private calibratePoints: Point2D[] = [];
  private calibrateHover: Point2D | undefined;
  private canvasCursor: CursorKind = 'default';
  /** Ink-line snap index for the current raster (UV space). */
  private inkSnapIndex: BlueprintInkSnapIndex | undefined;
  private inkSnapImageUrl: string | undefined;
  private inkSnapLoadId = 0;
  /** Floor elevation (meters) of the storey currently being edited. */
  private elevationMeters = 0;
  /**
   * Hide the raster without changing persisted {@link BlueprintUnderlay.visible}
   * (location-pin mode).
   */
  private displaySuppressed = false;
  private elevatedGl: BlueprintElevatedGlResources | undefined;
  private elevatedGlContext: WebGLRenderingContext | undefined;
  private elevatedTextureUrl: string | undefined;
  private elevatedTextureLoadId = 0;
  private elevatedLayer: mapboxgl.CustomLayerInterface | undefined;

  attach(map: Map): void {
    this.map = map;
    this.elevatedTextureUrl = undefined;
    this.ensureLayers();
    this.syncToMap();
    this.attachListeners();
    this.applyCursor('default');
  }

  detach(): void {
    this.endDrag({ commit: false });
    this.detachListeners();
    this.applyCursor('default');
    this.removeElevatedLayer();
    this.map = undefined;
    this.elevatedTextureUrl = undefined;
    this.elevatedTextureLoadId += 1;
    this.spaceHeld = false;
    this.isPanningWithSpace = false;
    this.calibratePoints = [];
    this.calibrateHover = undefined;
    this.inkSnapIndex = undefined;
    this.inkSnapImageUrl = undefined;
    this.inkSnapLoadId += 1;
  }

  /**
   * Build / refresh the ink snap index when the raster data URL changes.
   */
  private async ensureInkSnapIndex(
    underlay: BlueprintUnderlay | undefined,
  ): Promise<void> {
    if (!underlay?.imageDataUrl) {
      this.inkSnapIndex = undefined;
      this.inkSnapImageUrl = undefined;
      return;
    }
    if (underlay.imageDataUrl === this.inkSnapImageUrl && this.inkSnapIndex) {
      return;
    }

    const loadId = ++this.inkSnapLoadId;
    const imageUrl = underlay.imageDataUrl;
    try {
      const imageData = await loadBlueprintImageData(imageUrl);
      if (loadId !== this.inkSnapLoadId) return;
      if (!imageData) {
        this.inkSnapIndex = undefined;
        this.inkSnapImageUrl = undefined;
        return;
      }
      this.inkSnapIndex = extractBlueprintInkSnapIndex(imageData);
      this.inkSnapImageUrl = imageUrl;
    } catch (err) {
      console.warn('Failed to build blueprint ink snap index', err);
      if (loadId !== this.inkSnapLoadId) return;
      this.inkSnapIndex = undefined;
      this.inkSnapImageUrl = undefined;
    }
  }

  setLocation(location: Coordinate | undefined): void {
    this.location = location;
    this.syncToMap();
  }

  /**
   * Place the underlay plane at this storey floor elevation (meters above design datum).
   */
  setElevationMeters(elevationMeters: number): void {
    if (
      !Number.isFinite(elevationMeters) ||
      this.elevationMeters === elevationMeters
    ) {
      return;
    }
    this.elevationMeters = elevationMeters;
    this.syncToMap();
  }

  setUnderlay(underlay: BlueprintUnderlay | undefined): void {
    // Skip visual no-ops, but always keep the latest object (e.g. storey mapping
    // for elevation) so {@link DesignMap.syncBlueprintUnderlayPresentation} sees it.
    if (isSameBlueprintUnderlayForMap(this.underlay, underlay)) {
      this.underlay = underlay;
      return;
    }
    this.underlay = underlay;
    if (!underlay) {
      // Drop in-progress measure so a stale length chip cannot linger in React.
      this.calibratePoints = [];
      this.calibrateHover = undefined;
    }
    this.syncToMap();
    void this.ensureInkSnapIndex(underlay);
  }

  /**
   * Update underlay placement and notify the React persistence handler.
   */
  updateUnderlayAndNotify(underlay: BlueprintUnderlay): void {
    this.underlay = underlay;
    this.syncToMap();
    void this.ensureInkSnapIndex(underlay);
    this.onChange?.(underlay);
  }

  getUnderlay(): BlueprintUnderlay | undefined {
    return this.underlay;
  }

  /**
   * Hide or restore the PDF raster without changing persisted visibility.
   */
  setDisplaySuppressed(suppressed: boolean): void {
    if (this.displaySuppressed === suppressed) return;
    this.displaySuppressed = suppressed;
    this.syncToMap();
  }

  /** True when the PDF should paint and accept pointer hits. */
  private isUnderlayDisplayed(): boolean {
    return Boolean(this.underlay?.visible) && !this.displaySuppressed;
  }

  /**
   * Snap a local-meter point to dark lines inside the blueprint image.
   * Does not use the outer page frame — only ink detected in the raster.
   */
  snapToInk(
    point: Point2D,
    maxDistanceMeters: number,
  ): { point: Point2D; snapped: boolean } {
    const underlay = this.underlay;
    const index = this.inkSnapIndex;
    if (!underlay || !this.isUnderlayDisplayed() || !index) {
      return { point, snapped: false };
    }
    return snapMetricPointToBlueprintInk(
      underlay,
      index,
      point,
      maxDistanceMeters,
    );
  }

  setMode(mode: BlueprintInteractionMode): void {
    this.mode = mode;
    if (mode !== 'transform') this.endDrag({ commit: false });
    if (mode !== 'calibrate') {
      this.calibratePoints = [];
      this.calibrateHover = undefined;
    }
    this.syncOutline();
    this.syncCalibrateOverlay();
    this.refreshCursor();
  }

  getMode(): BlueprintInteractionMode {
    return this.mode;
  }

  /**
   * True while transform/calibrate should swallow map draft clicks.
   * Space-pan temporarily yields pointer to map drag.
   */
  isCapturingPointer(): boolean {
    if (this.spaceHeld) return false;
    return this.mode !== 'locked' && this.isUnderlayDisplayed();
  }

  /**
   * True when {@link point} lies inside the visible underlay frame (local meters).
   */
  hitTestMetric(point: Point2D): boolean {
    const underlay = this.underlay;
    if (!underlay || !this.isUnderlayDisplayed()) return false;
    return isPointInsideUnderlay(underlay, point);
  }

  setOnChange(handler: BlueprintChangeHandler | undefined): void {
    this.onChange = handler;
  }

  setOnCalibratePoint(handler: CalibratePointHandler | undefined): void {
    this.onCalibratePoint = handler;
  }

  setOnCalibrateLabels(handler: CalibrateLabelsHandler | undefined): void {
    this.onCalibrateLabels = handler;
    // Re-push so React cannot keep a stale chip after handler (re)attach.
    handler?.(this.lastCalibrateLabels);
  }

  setOnEscape(handler: EscapeHandler | undefined): void {
    this.onEscape = handler;
  }

  /**
   * Sync calibration endpoints from React (for rubber-band + Enter accept UX).
   */
  setCalibratePoints(points: Point2D[]): void {
    this.calibratePoints = points.slice(0, 2);
    if (this.calibratePoints.length >= 2) this.calibrateHover = undefined;
    this.syncCalibrateOverlay();
  }

  /**
   * Clear the calibrate rubber-band / points. Returns true when there was something to clear.
   */
  clearCalibratePreview(): boolean {
    const hadPreview =
      this.calibratePoints.length > 0 || this.calibrateHover !== undefined;
    this.calibratePoints = [];
    this.calibrateHover = undefined;
    this.syncCalibrateOverlay();
    return hadPreview;
  }

  /**
   * Stepwise Escape for blueprint tools.
   * Calibrate: first clears measure points (stay in ruler mode), then exits to wall draw.
   * Transform: first cancels an active drag, then exits to wall draw.
   * @returns true when Escape was consumed (caller should not also cancel a wall draft).
   */
  handleEscape(): boolean {
    if (this.mode === 'calibrate') {
      if (
        this.calibratePoints.length > 0 ||
        this.calibrateHover !== undefined
      ) {
        this.clearCalibratePreview();
        this.onEscape?.('cleared-points');
        return true;
      }
      this.onEscape?.('exited-mode');
      return true;
    }

    if (this.mode === 'transform') {
      if (this.drag) {
        this.endDrag({ commit: false });
        return true;
      }
      this.onEscape?.('exited-mode');
      return true;
    }

    return false;
  }

  /**
   * Recreate dropped layers after a Mapbox style reload, without mutating
   * paint/order when the stack is already correct (avoids a styledata loop).
   */
  handleStyleData(): void {
    const map = this.map;
    if (!map) return;
    const needsResync = shouldResyncBlueprintAfterStyleData({
      underlayVisible: this.isUnderlayDisplayed(),
      hasElevatedLayer: Boolean(
        map.getLayer(MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID),
      ),
      hasOutlineSource: Boolean(
        map.getSource(MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID),
      ),
      hasOutlineLayer: Boolean(map.getLayer(MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID)),
      hasHandlesLayer: Boolean(map.getLayer(MAPBOX_BLUEPRINT_HANDLES_LAYER_ID)),
      hasCalibrateSource: Boolean(
        map.getSource(MAPBOX_BLUEPRINT_CALIBRATE_SOURCE_ID),
      ),
      hasCalibrateLineLayer: Boolean(
        map.getLayer(MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID),
      ),
    });
    this.ensureLayers();
    if (needsResync) this.syncToMap();
  }

  ensureLayers(): void {
    const map = this.map;
    if (!map) return;

    try {
      // styledata can reshuffle custom layers — park PDF under three.js again.
      this.ensureElevatedLayerOrder();

      if (!map.getSource(MAPBOX_BLUEPRINT_SOURCE_ID)) {
        // No url: ImageSource.load fetches every url, including a data: PNG,
        // and a failed fetch is emitted as a map error. The PDF is drawn by
        // the elevated layer; this source only holds a slot in the layer stack.
        map.addSource(MAPBOX_BLUEPRINT_SOURCE_ID, {
          type: 'image',
          coordinates: [
            [0, 0],
            [0, 0],
            [0, 0],
            [0, 0],
          ],
        });
      }

      if (!map.getLayer(MAPBOX_BLUEPRINT_LAYER_ID)) {
        map.addLayer(
          {
            id: MAPBOX_BLUEPRINT_LAYER_ID,
            type: 'raster',
            source: MAPBOX_BLUEPRINT_SOURCE_ID,
            paint: {
              'raster-opacity': 0.4,
              'raster-fade-duration': 0,
            },
            layout: { visibility: 'none' },
          },
          resolveBlueprintBeforeId(map),
        );
      }

      ensureGeoJsonSourceWithLineMetrics(
        map,
        MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID,
        [MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID, MAPBOX_BLUEPRINT_HANDLES_LAYER_ID],
      );

      if (!map.getLayer(MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID)) {
        map.addLayer(
          {
            id: MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID,
            type: 'line',
            source: MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID,
            filter: ['==', ['get', 'role'], 'outline'],
            paint: {
              // Selection chrome — same focus blue as measure / handles.
              'line-color': BLUEPRINT_CALIBRATE_COLOR,
              'line-width': 1.5,
              'line-opacity': 0.95,
            },
            layout: {
              visibility: 'none',
              'line-cap': 'square',
              'line-join': 'miter',
              // Keep the frame coplanar with the elevated PDF plane.
              'line-elevation-reference': 'ground',
              'line-z-offset': this.elevationMeters,
            },
          },
          resolveBlueprintBeforeId(map),
        );
      } else {
        this.applyLineLayerElevation(MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID);
      }

      if (!map.getLayer(MAPBOX_BLUEPRINT_HANDLES_LAYER_ID)) {
        map.addLayer(
          {
            id: MAPBOX_BLUEPRINT_HANDLES_LAYER_ID,
            type: 'circle',
            source: MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID,
            filter: ['==', ['get', 'role'], 'handle'],
            paint: {
              'circle-radius': 6,
              'circle-color': '#ffffff',
              'circle-stroke-color': BLUEPRINT_CALIBRATE_COLOR,
              'circle-stroke-width': 2,
            },
            layout: { visibility: 'none' },
          },
          resolveBlueprintBeforeId(map),
        );
      }

      ensureGeoJsonSourceWithLineMetrics(
        map,
        MAPBOX_BLUEPRINT_CALIBRATE_SOURCE_ID,
        [MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID],
      );

      if (!map.getLayer(MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID)) {
        map.addLayer(
          {
            id: MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID,
            type: 'line',
            source: MAPBOX_BLUEPRINT_CALIBRATE_SOURCE_ID,
            filter: [
              'any',
              ['==', ['get', 'role'], 'calibrate-line'],
              ['==', ['get', 'role'], 'calibrate-tick'],
            ],
            paint: {
              'line-color': BLUEPRINT_CALIBRATE_COLOR,
              'line-width': [
                'match',
                ['get', 'role'],
                'calibrate-tick',
                CALIBRATE_TICK_WIDTH,
                CALIBRATE_LINE_WIDTH,
              ],
              'line-opacity': CALIBRATE_LINE_OPACITY,
            },
            layout: {
              visibility: 'none',
              'line-cap': 'square',
              'line-join': 'miter',
              'line-elevation-reference': 'ground',
              'line-z-offset': this.elevationMeters,
            },
          },
          resolveBlueprintBeforeId(map),
        );
      } else {
        this.applyLineLayerElevation(MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID);
      }

      this.ensureBlueprintLayerOrder();
    } catch (err) {
      console.warn('Failed to ensure blueprint underlay layers', err);
    }
  }

  private syncToMap(): void {
    const map = this.map;
    const underlay = this.underlay;
    const location = this.location;
    if (!map) return;

    this.ensureLayers();

    if (!underlay || !location || !this.isUnderlayDisplayed()) {
      this.setGroundRasterVisibility('none');
      this.syncElevatedLayer({ visible: false });
      this.syncOutline();
      this.syncCalibrateOverlay();
      return;
    }

    // Always elevated so PDF height follows storey elevation (incl. below-grade
    // storey 1). The ground-draped raster is locked to map 0 and cannot.
    this.setGroundRasterVisibility('none');
    this.syncElevatedLayer({
      visible: true,
      underlay,
      location,
      elevationMeters: this.elevationMeters,
    });

    this.syncOutline();
    this.syncCalibrateOverlay();
  }

  /**
   * Show or hide the ground-draped Mapbox raster underlay layer.
   */
  private setGroundRasterVisibility(visibility: 'visible' | 'none'): void {
    const map = this.map;
    if (!map?.getLayer(MAPBOX_BLUEPRINT_LAYER_ID)) return;
    try {
      setLayerVisibilityIfChanged(
        map,
        MAPBOX_BLUEPRINT_LAYER_ID,
        visibility === 'visible',
      );
    } catch {
      // layer may be missing during style swap
    }
  }

  /**
   * Sync the elevated WebGL underlay plane (storey-floor height).
   */
  private syncElevatedLayer(
    next:
      | { visible: false }
      | {
          visible: true;
          underlay: BlueprintUnderlay;
          location: Coordinate;
          elevationMeters: number;
        },
  ): void {
    const map = this.map;
    if (!map) return;

    if (!next.visible) {
      if (map.getLayer(MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID)) {
        // Keep the layer registered; render becomes a no-op when state is hidden.
        map.triggerRepaint();
      }
      this.elevatedLayerState = {
        underlay: undefined,
        location: undefined,
        elevationMeters: 0,
        textureUrl: undefined,
        visible: false,
      };
      return;
    }

    this.ensureElevatedLayer();
    this.elevatedLayerState = {
      underlay: next.underlay,
      location: next.location,
      elevationMeters: next.elevationMeters,
      textureUrl: next.underlay.imageDataUrl,
      visible: true,
    };

    const textureUrl = next.underlay.imageDataUrl;
    if (textureUrl && this.elevatedTextureUrl !== textureUrl) {
      void this.loadElevatedTexture(textureUrl);
    }
    map.triggerRepaint();
  }

  private elevatedLayerState: BlueprintElevatedLayerState = {
    underlay: undefined,
    location: undefined,
    elevationMeters: 0,
    textureUrl: undefined,
    visible: false,
  };

  /**
   * Layer the elevated PDF must sit under so storey fills/lines paint on top.
   * Prefer three.js extrusion; fall back through later design overlays.
   */
  private resolveElevatedBeforeId(): string | undefined {
    const map = this.map;
    if (!map) return undefined;
    if (map.getLayer('map-threejs-extrusion')) return 'map-threejs-extrusion';
    if (map.getLayer('map-threejs-wireframe')) return 'map-threejs-wireframe';
    if (map.getLayer('map-threejs-points')) return 'map-threejs-points';
    if (map.getLayer(MapLayerIds.Points)) return MapLayerIds.Points;
    return undefined;
  }

  /**
   * Keep the elevated PDF immediately under three.js so 100% opacity cannot
   * cover selected-storey program fills or building lines after styledata moves.
   */
  private ensureElevatedLayerOrder(): void {
    const map = this.map;
    if (!map?.getLayer(MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID)) return;
    const beforeId = this.resolveElevatedBeforeId();
    if (!beforeId) return;
    try {
      moveLayerBeforeIfNeeded(
        map,
        MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID,
        beforeId,
      );
    } catch {
      // Layer stack may be mid-reload
    }
  }

  /**
   * Register the elevated custom layer once (below three.js extrusion).
   */
  private ensureElevatedLayer(): void {
    const map = this.map;
    if (!map) return;

    if (!map.getLayer(MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID)) {
      const layer: mapboxgl.CustomLayerInterface = {
        id: MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID,
        type: 'custom',
        renderingMode: '3d',
        onAdd: (_mapInstance, gl) => {
          this.elevatedGlContext = gl;
          this.elevatedGl = initBlueprintElevatedGl(gl);
          if (
            this.elevatedLayerState.visible &&
            this.elevatedLayerState.textureUrl
          ) {
            void this.loadElevatedTexture(this.elevatedLayerState.textureUrl);
          }
        },
        render: (gl, matrix) => {
          const resources = this.elevatedGl;
          if (!resources || !this.elevatedLayerState.visible) return;
          const location = this.elevatedLayerState.location;
          if (!location) return;
          const origin = mapboxgl.MercatorCoordinate.fromLngLat(location, 0);
          renderBlueprintElevatedLayer(
            gl,
            matrix,
            resources,
            this.elevatedLayerState,
            origin,
          );
        },
        onRemove: (_mapInstance, gl) => {
          if (this.elevatedGl) {
            destroyBlueprintElevatedGl(gl, this.elevatedGl);
            this.elevatedGl = undefined;
          }
          this.elevatedGlContext = undefined;
        },
      };

      this.elevatedLayer = layer;
      map.addLayer(layer, this.resolveElevatedBeforeId());
    }

    this.ensureElevatedLayerOrder();
  }

  /**
   * Drop the elevated custom layer (detach / style teardown).
   */
  private removeElevatedLayer(): void {
    const map = this.map;
    if (!map) return;
    if (map.getLayer(MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID)) {
      map.removeLayer(MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID);
    }
    this.elevatedLayer = undefined;
    this.elevatedGl = undefined;
    this.elevatedLayerState = {
      underlay: undefined,
      location: undefined,
      elevationMeters: 0,
      textureUrl: undefined,
      visible: false,
    };
  }

  /**
   * Async-load the underlay JPEG into the elevated GL texture.
   */
  private async loadElevatedTexture(imageDataUrl: string): Promise<void> {
    const loadId = ++this.elevatedTextureLoadId;
    const map = this.map;
    const gl = this.elevatedGlContext;
    const resources = this.elevatedGl;
    if (!map || !gl || !resources) return;

    try {
      await loadBlueprintElevatedTexture(gl, resources.texture, imageDataUrl);
      if (loadId !== this.elevatedTextureLoadId) return;
      this.elevatedTextureUrl = imageDataUrl;
      map.triggerRepaint();
    } catch (err) {
      console.warn('Failed to load elevated blueprint underlay texture', err);
    }
  }

  /**
   * Keep the underlay above the basemap mask and below building fill when
   * possible, with outline / handles / measure chrome on top of the plan.
   */
  private ensureBlueprintLayerOrder(): void {
    const map = this.map;
    if (!map?.getLayer(MAPBOX_BLUEPRINT_LAYER_ID)) return;

    const beforeId = resolveBlueprintBeforeId(map);
    try {
      if (beforeId) {
        moveLayerBeforeIfNeeded(map, MAPBOX_BLUEPRINT_LAYER_ID, beforeId);
        stackLayersOnTop(map, [
          MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID,
          MAPBOX_BLUEPRINT_HANDLES_LAYER_ID,
          MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID,
        ]);
        return;
      }
      stackLayersOnTop(map, [
        MAPBOX_BLUEPRINT_LAYER_ID,
        MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID,
        MAPBOX_BLUEPRINT_HANDLES_LAYER_ID,
        MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID,
      ]);
    } catch {
      // Layer stack may be mid-reload
    }
  }

  /**
   * Keep Mapbox line chrome (selection / calibrate) at the underlay floor height.
   */
  private applyLineLayerElevation(layerId: string): void {
    const map = this.map;
    if (!map?.getLayer(layerId)) return;
    try {
      setLayoutPropertyIfChanged(
        map,
        layerId,
        'line-elevation-reference',
        'ground',
      );
      setLayoutPropertyIfChanged(
        map,
        layerId,
        'line-z-offset',
        this.elevationMeters,
      );
    } catch {
      // Style may be mid-reload.
    }
  }

  private syncOutline(): void {
    const map = this.map;
    const underlay = this.underlay;
    const location = this.location;
    if (!map) return;

    const outlineSource = map.getSource(MAPBOX_BLUEPRINT_OUTLINE_SOURCE_ID) as
      GeoJSONSource | undefined;
    if (!outlineSource) return;

    // Selected / calibrating PDF: thin blue frame (corner rotate uses cursor only).
    const showSelection =
      (this.mode === 'transform' || this.mode === 'calibrate') &&
      this.isUnderlayDisplayed() &&
      Boolean(location);

    // Legacy resize dots stay off — rotate is hover-cursor + drag near corners.
    setLayerVisibility(map, MAPBOX_BLUEPRINT_HANDLES_LAYER_ID, false);
    this.applyLineLayerElevation(MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID);

    if (!showSelection || !underlay || !location) {
      outlineSource.setData(emptyFeatureCollection());
      setLayerVisibility(map, MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID, false);
      return;
    }

    const corners = getBlueprintCornerOffsets(underlay);
    const lngLats = corners.map((offset) => toLngLat(location, offset));
    const first = lngLats[0];
    if (!first) return;
    // Close the ring for the selection outline.
    const ring = [...lngLats, first];

    outlineSource.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature' as const,
          properties: { role: 'outline' },
          geometry: { type: 'LineString' as const, coordinates: ring },
        },
      ],
    });

    setLayerVisibility(map, MAPBOX_BLUEPRINT_OUTLINE_LAYER_ID, true);
  }

  private syncCalibrateOverlay(): void {
    const map = this.map;
    const location = this.location;
    if (!map) return;

    const source = map.getSource(MAPBOX_BLUEPRINT_CALIBRATE_SOURCE_ID) as
      GeoJSONSource | undefined;
    if (!source) return;

    this.applyLineLayerElevation(MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID);

    // The measure line is set-scale chrome only — nothing is drawn otherwise.
    if (!location || !this.isUnderlayDisplayed() || this.mode !== 'calibrate') {
      source.setData(emptyFeatureCollection());
      setLayerVisibility(map, MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID, false);
      this.emitCalibrateLabels([]);
      return;
    }

    const features: GeoJSON.Feature[] = [];
    const labels: BlueprintCalibrateLabel[] = [];

    // Active rubber-band while placing (cursor-only — no endpoint dots).
    const endpoints = [...this.calibratePoints];
    if (endpoints.length === 1 && this.calibrateHover) {
      endpoints.push(this.calibrateHover);
    }
    const [start, end] = endpoints;
    if (start && end) {
      for (const segment of buildCalibrateMeasureSegments(start, end)) {
        features.push({
          type: 'Feature',
          properties: { role: segment.role },
          geometry: {
            type: 'LineString',
            coordinates: [
              toLngLat(location, segment.start),
              toLngLat(location, segment.end),
            ],
          },
        });
      }
      // Anchor for the on-map length popup below the span.
      const midpoint: Point2D = [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
      ];
      const screen = this.projectMetricToScreen(location, midpoint);
      if (screen) {
        labels.push({
          screenPoint: screen,
          text: '',
          kind: 'pending-input',
        });
      }
    }

    source.setData({ type: 'FeatureCollection', features });
    setLayerVisibility(
      map,
      MAPBOX_BLUEPRINT_CALIBRATE_LINE_LAYER_ID,
      features.length > 0,
    );
    this.emitCalibrateLabels(labels);
  }

  /**
   * Notify React of calibrate labels only when content actually changes.
   */
  private emitCalibrateLabels(labels: BlueprintCalibrateLabel[]): void {
    if (areCalibrateLabelsEqual(this.lastCalibrateLabels, labels)) return;
    this.lastCalibrateLabels = labels;
    this.onCalibrateLabels?.(labels);
  }

  /**
   * Project a local-meter point to map screen pixels.
   */
  private projectMetricToScreen(
    location: Coordinate,
    point: Point2D,
  ): Point2D | undefined {
    if (!this.map) return undefined;
    const [lng, lat] = toLngLat(location, point);
    const projected = this.map.project([lng, lat]);
    return [projected.x, projected.y];
  }

  private attachListeners(): void {
    const map = this.map;
    if (!map || this.listenersAttached) return;
    this.listenersAttached = true;
    map.on('mousedown', this.handleMouseDown);
    map.on('mousemove', this.handleMouseMove);
    map.on('mouseup', this.handleMouseUp);
    map.on('mouseleave', this.handleMouseUp);
    // Keep calibration labels glued to the drawing while the camera moves.
    map.on('move', this.handleMapMove);
  }

  private detachListeners(): void {
    const map = this.map;
    if (!map || !this.listenersAttached) return;
    this.listenersAttached = false;
    map.off('mousedown', this.handleMouseDown);
    map.off('mousemove', this.handleMouseMove);
    map.off('mouseup', this.handleMouseUp);
    map.off('mouseleave', this.handleMouseUp);
    map.off('move', this.handleMapMove);
  }

  private readonly handleMapMove = (): void => {
    if (this.mode !== 'calibrate') return;
    this.syncCalibrateOverlay();
  };

  /**
   * Yield transform/calibrate clicks while DesignMap Space-pan is active.
   */
  setSpacePanActive(active: boolean): void {
    if (this.spaceHeld === active) return;
    this.spaceHeld = active;
    if (!active) this.isPanningWithSpace = false;
    this.refreshCursor();
  }

  /**
   * Finish or cancel a transform drag and always restore Mapbox pan.
   */
  private endDrag(options: { commit: boolean }): void {
    this.map?.dragPan.enable();
    const drag = this.drag;
    if (!drag) return;
    this.drag = undefined;
    if (!options.commit) {
      this.underlay = drag.startUnderlay;
      this.syncToMap();
      this.refreshCursor();
      return;
    }
    if (this.underlay) this.onChange?.(this.underlay);
    this.refreshCursor();
  }

  private readonly handleMouseDown = (event: MapMouseEvent): void => {
    const map = this.map;
    if (!map) return;

    // Middle mouse is reserved for map pan (hand cursor); never start transform.
    if (event.originalEvent.button === 1) return;

    if (this.spaceHeld) {
      this.isPanningWithSpace = true;
      this.applyCursor('grabbing');
      return;
    }

    // Geometry/transform interactions are primary-button only.
    if (event.originalEvent.button !== 0) return;

    if (!this.isCapturingPointer()) return;
    const underlay = this.underlay;
    const location = this.location;
    if (!underlay || !location) return;

    const raw = eventToMetric(location, event);

    if (this.mode === 'calibrate') {
      event.preventDefault();
      // Free placement — no ink / axis snap while measuring.
      if (this.calibratePoints.length < 2) {
        this.calibratePoints = [...this.calibratePoints, raw].slice(0, 2);
        if (this.calibratePoints.length >= 2) this.calibrateHover = undefined;
        this.syncCalibrateOverlay();
      }
      this.onCalibratePoint?.(raw);
      return;
    }

    if (this.mode !== 'transform') return;

    // Near a corner: rotate; interior drag moves the page.
    if (isNearRotateHandle(map, location, underlay, event.point)) {
      event.preventDefault();
      this.drag = {
        kind: 'rotate',
        startPointer: raw,
        startUnderlay: underlay,
        startPointerAngleDeg: polarAngleDeg(underlay.center, raw),
        stickySnap: {},
      };
      map.dragPan.disable();
      this.applyCursor('rotate');
      return;
    }

    if (!isPointInsideUnderlay(underlay, raw)) return;

    event.preventDefault();
    this.drag = {
      kind: 'move',
      startPointer: raw,
      startUnderlay: underlay,
    };
    map.dragPan.disable();
    this.applyCursor('grabbing');
  };

  private readonly handleMouseMove = (event: MapMouseEvent): void => {
    const map = this.map;
    const location = this.location;
    if (!map || !location) return;

    if (this.spaceHeld) {
      this.applyCursor(this.isPanningWithSpace ? 'grabbing' : 'grab');
      return;
    }

    if (this.mode === 'calibrate' && this.underlay) {
      this.calibrateHover = eventToMetric(location, event);
      this.syncCalibrateOverlay();
      this.applyCursor('crosshair');
      return;
    }

    if (this.drag && this.underlay) {
      const point = eventToMetric(location, event);
      const { startPointer, startUnderlay } = this.drag;
      if (this.drag.kind === 'rotate') {
        const pointerAngleDeg = polarAngleDeg(startUnderlay.center, point);
        const rawRotationDeg =
          startUnderlay.rotationDeg +
          (pointerAngleDeg - this.drag.startPointerAngleDeg);
        // Shift disables soft snap (same convention as design geometry snap).
        if (event.originalEvent.shiftKey) {
          this.drag = { ...this.drag, stickySnap: {} };
          this.underlay = {
            ...startUnderlay,
            rotationDeg: rawRotationDeg,
          };
          this.syncToMap();
          return;
        }
        const snapped = softSnapDegreesToStep(
          rawRotationDeg,
          ROTATION_SNAP_STEP_DEG,
          {
            enterDeg: ROTATION_SOFT_SNAP_ENTER_DEG,
            breakoutDeg: ROTATION_SOFT_SNAP_BREAKOUT_DEG,
            sticky: this.drag.stickySnap,
          },
        );
        this.drag = { ...this.drag, stickySnap: snapped.state };
        this.underlay = {
          ...startUnderlay,
          rotationDeg: snapped.degrees,
        };
        this.syncToMap();
        return;
      }

      const dx = point[0] - startPointer[0];
      const dy = point[1] - startPointer[1];
      this.underlay = moveBlueprintUnderlay(startUnderlay, [
        startUnderlay.center[0] + dx,
        startUnderlay.center[1] + dy,
      ]);
      this.syncToMap();
      return;
    }

    this.refreshCursor(event);
  };

  private readonly handleMouseUp = (): void => {
    if (this.isPanningWithSpace) {
      this.isPanningWithSpace = false;
      this.refreshCursor();
    }
    this.endDrag({ commit: true });
  };

  private refreshCursor(event?: MapMouseEvent): void {
    if (this.spaceHeld) {
      this.applyCursor(this.isPanningWithSpace ? 'grabbing' : 'grab');
      return;
    }
    if (this.mode === 'calibrate') {
      this.applyCursor('crosshair');
      return;
    }
    if (this.mode === 'transform' && this.map && event) {
      const underlay = this.underlay;
      const location = this.location;
      if (underlay && location) {
        if (isNearRotateHandle(this.map, location, underlay, event.point)) {
          this.applyCursor('rotate');
          return;
        }
        const point = eventToMetric(location, event);
        if (isPointInsideUnderlay(underlay, point)) {
          this.applyCursor('move');
          return;
        }
      }
    }
    this.applyCursor('default');
  }

  private applyCursor(kind: CursorKind): void {
    if (this.canvasCursor === kind) return;
    this.canvasCursor = kind;
    const canvas = this.map?.getCanvas();
    if (!canvas) return;
    if (kind === 'default') {
      canvas.style.cursor = '';
      return;
    }
    if (kind === 'rotate') {
      canvas.style.cursor = BLUEPRINT_ROTATE_CURSOR;
      return;
    }
    canvas.style.cursor = kind;
  }
}

const eventToMetric = (
  location: Coordinate,
  event: Pick<MapMouseEvent, 'lngLat'>,
): Point2D => {
  const [mx, my] = convertCoordinateToMetricOffset(event.lngLat, location);
  return [mx, -my];
};

const toLngLat = (location: Coordinate, offset: Point2D): [number, number] => {
  const { lng, lat } = convertMetricOffsetToCoordinate(location, offset);
  return [lng, lat];
};

const isPointInsideUnderlay = (
  underlay: BlueprintUnderlay,
  point: Point2D,
): boolean => {
  const [cx, cy] = underlay.center;
  const local: Point2D = [point[0] - cx, point[1] - cy];
  const unrotated = rotatePoint2DClockwise(local, -underlay.rotationDeg);
  const halfW = underlay.widthMeters / 2;
  const halfH = underlay.heightMeters / 2;
  return Math.abs(unrotated[0]) <= halfW && Math.abs(unrotated[1]) <= halfH;
};

/**
 * Polar angle in degrees from {@link origin} to {@link tip}.
 * Uses the same sign convention as {@link rotatePoint2DClockwise}.
 */
const polarAngleDeg = (origin: Point2D, tip: Point2D): number => {
  const dx = tip[0] - origin[0];
  const dy = tip[1] - origin[1];
  return (Math.atan2(dy, dx) * 180) / Math.PI;
};

/**
 * True when the screen pointer is within the corner rotate-handle hit radius.
 */
const isNearRotateHandle = (
  map: Map,
  location: Coordinate,
  underlay: BlueprintUnderlay,
  screenPoint: { x: number; y: number },
): boolean => {
  const corners = getBlueprintCornerOffsets(underlay);
  for (const corner of corners) {
    const [lng, lat] = toLngLat(location, corner);
    const projected = map.project([lng, lat]);
    const dist = getDistance(
      [screenPoint.x, screenPoint.y],
      [projected.x, projected.y],
    );
    if (dist <= ROTATE_HANDLE_HIT_PX) return true;
  }
  return false;
};

const resolveBlueprintBeforeId = (map: Map): string | undefined => {
  if (map.getLayer(MapLayerIds.BuildingFill2d)) {
    return MapLayerIds.BuildingFill2d;
  }
  return undefined;
};

const setLayerVisibility = (
  map: Map,
  layerId: string,
  visible: boolean,
): void => {
  try {
    setLayerVisibilityIfChanged(map, layerId, visible);
  } catch {
    // ignore missing layer during style reload
  }
};

const emptyFeatureCollection = (): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: [],
});

const isGeoJsonSourceSpecification = (
  value: unknown,
): value is GeoJSONSourceSpecification =>
  guards.is.object(value) && value.type === 'geojson';

/**
 * GeoJSON source for elevated line layers. Mapbox needs {@link GeoJSONSourceSpecification.lineMetrics}
 * when it evaluates line-progress (`line-z-offset`). Recreates a source that was added without it.
 */
const ensureGeoJsonSourceWithLineMetrics = (
  map: Map,
  sourceId: string,
  dependentLayerIds: readonly string[],
): void => {
  const existing = map.getSource(sourceId);
  if (existing?.type === 'geojson') {
    const spec = existing.serialize();
    if (isGeoJsonSourceSpecification(spec) && spec.lineMetrics === true) {
      return;
    }

    for (const layerId of dependentLayerIds) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    }
    const data =
      isGeoJsonSourceSpecification(spec) && !guards.is.string(spec.data)
        ? spec.data
        : emptyFeatureCollection();
    map.removeSource(sourceId);
    map.addSource(sourceId, {
      type: 'geojson',
      data,
      lineMetrics: true,
    });
    return;
  }

  map.addSource(sourceId, {
    type: 'geojson',
    data: emptyFeatureCollection(),
    lineMetrics: true,
  });
};
